import { useEffect, useState, type FormEvent } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { useNavigate } from "react-router";
import SearchField from "../../interface/shared/searchfield";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { ProviderUsers, useUsers } from "../../api/users";
import { FadeIn } from "../../interface/shared/animations";

export default function Users() {
    return (
        <DashboardLayout>
            <ProviderUsers>
                <UsersContent />
            </ProviderUsers>
        </DashboardLayout>
    );
}

function UsersContent() {
    const {
        users,
        errors,
        loading,
        fetchUsers,
        page,
        perPage,
        total,
        setPage,
        setPerPage,
        searchTerm,
        setSearchTerm,
        sortField,
        sortOrder,
        setSort,
    } = useUsers();
    const navigate = useNavigate();
    const [createOpen, setCreateOpen] = useState(false);

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Users
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All users with access to the warehouse system.
                    </Typography>
                </Box>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                    <SearchField
                        placeholder="Search users by name or email address"
                        value={searchTerm}
                        onChange={setSearchTerm}
                    />
                    <Button
                        variant="contained"
                        startIcon={<PersonAddRoundedIcon />}
                        onClick={() => setCreateOpen(true)}
                    >
                        Add user
                    </Button>

                </Stack>
            </Stack>

            <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => fetchUsers()}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell align="right">
                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                <Skeleton variant="rounded" width={70} height={30} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sortDirection={sortField === "email" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "email"}
                                                direction={sortField === "email" ? sortOrder : "asc"}
                                                onClick={() => setSort("email")}
                                            >
                                                Email
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "name" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "name"}
                                                direction={sortField === "name" ? sortOrder : "asc"}
                                                onClick={() => setSort("name")}
                                            >
                                                Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                    No users found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : users.map((user) => (
                                        <TableRow key={`${user.id}`}>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                    onClick={() => navigate(`/users/${user.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { createUser, creating } = useUsers();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [localError, setLocalError] = useState<string | null>(null);
    const [apiError, setApiError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            setEmail("");
            setName("");
            setPassword("");
            setPasswordConfirmation("");
            setLocalError(null);
            setApiError(null);
        }
    }, [open]);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setLocalError(null);
        setApiError(null);

        const trimmedEmail = email.trim();
        const trimmedName = name.trim();
        if (!trimmedEmail) {
            setLocalError("Email is required.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setLocalError("Enter a valid email address.");
            return;
        }
        if (!trimmedName) {
            setLocalError("Name is required.");
            return;
        }
        if (!password) {
            setLocalError("Password is required.");
            return;
        }
        if (password !== passwordConfirmation) {
            setLocalError("Passwords do not match.");
            return;
        }

        const result = await createUser({
            email: trimmedEmail,
            name: trimmedName,
            password,
            password_confirmation: passwordConfirmation,
        });

        if (result.ok) {
            onClose();
        } else {
            setApiError(result.message);
        }
    }

    return (
        <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Add user</DialogTitle>
                <DialogContent>
                    {(localError || apiError) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {localError ?? apiError}
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={12}>
                            <TextField
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                required
                                size="small"
                                autoComplete="email"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                required
                                size="small"
                                autoComplete="name"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                required
                                size="small"
                                autoComplete="new-password"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Confirm password"
                                type="password"
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                fullWidth
                                required
                                size="small"
                                autoComplete="new-password"
                                disabled={creating}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button type="button" onClick={onClose} disabled={creating}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={creating}>
                        {creating ? "Creating…" : "Create user"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}